/* eslint-disable no-console */
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import { DebugConfig, getDebugModeConfig } from './debugConfig';
import './debugLogger';
import { DebugLogger } from './debugLogger';

type DebugSection = keyof Omit<DebugConfig, 'dumpLogs'>;

type Log = { section: DebugSection; name: string; args: unknown[] };

const debugSectionToColor = {
  auth: '#D3F8E2',
  storage: '#E4C1F9',
  state: '#F694C1',
  reducer: '#A9DEF9',
  user: '#A9DDD6',
} satisfies Record<DebugSection, string>;

export class DebugConsoleLogger {
  private debugConfig: DebugConfig;
  private unsubscribe: VoidFunction | null;

  constructor() {
    this.debugConfig = getDebugModeConfig();
    this.unsubscribe = null;
  }

  init() {
    const logs = DebugLogger.getLogs();

    if (this.debugConfig.dumpLogs && logs.length) {
      console.groupCollapsed('Dumped logs');

      for (const log of logs) {
        this.logToConsole(log, { trace: false, time: false });
      }

      console.groupEnd();
    }

    const { unsubscribe } = DebugLogger.subscribe(log => {
      this.logToConsole(log);
    });

    this.unsubscribe = unsubscribe;
  }

  disconnect() {
    this.unsubscribe?.();
  }

  private logToConsole(
    { name, section, args }: Log,
    { trace = true, time = true }: { trace?: boolean; time?: boolean } = {},
  ) {
    if (!this.debugConfig[section]) return;

    const log = [
      `%c${capitalize(section)}`,
      `color: black; background-color: ${debugSectionToColor[section]}; padding: 4px`,
      name,
      ...(time ? [format(Date.now(), 'mm:ss:SSS')] : []),
    ];

    console.groupCollapsed(...log);

    if (args.length) {
      console.groupCollapsed('Data');
      console.log(...args);
      console.groupEnd();
    }

    if (trace) {
      console.groupCollapsed('Trace');
      console.trace();
      console.groupEnd();
    }

    console.groupEnd();
  }
}
